import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories, siteConfig } from '@/mocks/comics';

const categorySlugMap: Record<string, string> = {
  all: '全部',
  action: '热血',
  romance: '恋爱',
  fantasy: '奇幻',
  thriller: '悬疑',
  comedy: '搞笑',
};

export default function Category() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug);
  const [filteredComics, setFilteredComics] = useState(comics);

  useEffect(() => {
    setActiveSlug(slug);
    if (slug === 'all') {
      setFilteredComics(comics);
    } else {
      const catName = categorySlugMap[slug];
      setFilteredComics(comics.filter((c) => c.category === catName));
    }
  }, [slug]);

  const catName = categorySlugMap[slug] || '全部';
  const pageTitle = `${catName}韩漫 - ${siteConfig.name}`;
  const pageDesc = `浏览${catName}类型的韩国漫画，${siteConfig.name}为您提供最新最全的${catName}韩漫免费在线阅读。`;

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: pageTitle,
            description: pageDesc,
            url: `${siteConfig.url}/category/${slug}`,
            isPartOf: { '@type': 'WebSite', name: siteConfig.name, url: siteConfig.url },
          }),
        }}
      />
      <Navbar />
      <main>
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: '分类' },
            { label: catName },
          ]}
        />

        <section className="max-w-7xl mx-auto px-4 md:px-6 pb-10 md:pb-14">
          <div className="mb-8">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
              {catName}韩漫
            </h1>
            <p className="text-sm text-foreground-500">
              共找到 <strong className="text-primary-600">{filteredComics.length}</strong> 部{catName === '全部' ? '' : catName}漫画
            </p>
          </div>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-secondary-100'
                }`}
              >
                <i className={`${cat.icon} text-xs`}></i>
                {cat.name}
                <span className="text-xs opacity-70">({cat.count})</span>
              </Link>
            ))}
          </div>

          <div
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5"
            data-product-shop=""
          >
            {filteredComics.map((comic) => (
              <ComicCard
                key={comic.id}
                id={comic.id}
                title={comic.title}
                cover={comic.cover}
                rating={comic.rating}
                category={comic.category}
                chapters={comic.chapters}
                status={comic.status}
                views={comic.views}
                tags={comic.tags}
              />
            ))}
          </div>

          {filteredComics.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-emotion-sad-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">该分类下暂无漫画，请查看其他分类</p>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
}